import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type ConsentRow = {
  consented_at: string;
  withdrawn_at: string | null;
  version: string;
};

const sb = supabase as any;

export async function getMyConsent(siteId: string): Promise<ConsentRow | null> {
  const { data, error } = await sb.rpc("get_my_concierge_consent", { _site_id: siteId });
  if (error) {
    console.warn("[concierge] getMyConsent error", error);
    return null;
  }
  const row = Array.isArray(data) ? data[0] : data;
  return (row as ConsentRow) ?? null;
}

export function useMyConsent(siteId: string | undefined) {
  return useQuery({
    queryKey: ["concierge", "consent", "me", siteId],
    queryFn: () => getMyConsent(siteId as string),
    enabled: !!siteId,
    staleTime: 30_000,
  });
}

export function isActive(row: ConsentRow | null | undefined): boolean {
  return !!row && !row.withdrawn_at;
}

export async function giveConsent(input: {
  site_id: string;
  coach_user_id: string | null;
}): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error("not_authenticated");

  // Upsert: if there's a withdrawn row for the same (user, site, version), clear withdrawn_at.
  const { data: existing } = await sb
    .from("concierge_consents")
    .select("id, withdrawn_at")
    .eq("user_id", uid)
    .eq("site_id", input.site_id)
    .eq("version", "v1")
    .maybeSingle();

  if (existing?.id) {
    const { error } = await sb
      .from("concierge_consents")
      .update({ withdrawn_at: null, consented_at: new Date().toISOString() })
      .eq("id", existing.id);
    if (error) throw error;
    return;
  }

  const { error } = await sb.from("concierge_consents").insert({
    user_id: uid,
    site_id: input.site_id,
    coach_user_id: input.coach_user_id,
    version: "v1",
  });
  if (error) throw error;
}

export async function withdrawConsent(siteId: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const uid = userData.user?.id;
  if (!uid) throw new Error("not_authenticated");
  const { error } = await sb
    .from("concierge_consents")
    .update({ withdrawn_at: new Date().toISOString() })
    .eq("user_id", uid)
    .eq("site_id", siteId);
  if (error) throw error;
}

export function useGiveConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: giveConsent,
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["concierge", "consent", "me", vars.site_id] });
    },
  });
}

export function useWithdrawConsent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: withdrawConsent,
    onSuccess: (_d, siteId) => {
      qc.invalidateQueries({ queryKey: ["concierge", "consent", "me", siteId] });
    },
  });
}
